import axios from "axios";
import * as Constants from "../../utils/Constants";
import {getAuthToken} from "../../utils/utility";

const setAuthHeader = () => {
  axios.defaults.headers.common["Authorization"] = `${
    Constants.BEARER
  } ${getAuthToken()}`;
};

export const fetchGlobalData = async (requesterId, pagination) => {
  setAuthHeader();

  const clientParams = pagination?.clients
    ? { page: pagination.clients.page, size: pagination.clients.size }
    : {};
  const assetParams = pagination?.assets
    ? { requesterId, page: pagination.assets.page, size: pagination.assets.size }
    : { requesterId };

  const clientsRes = await axios.get(
    `${Constants.BASE_URL}api/v1/clients/getAllClients`,
    { params: clientParams }
  );
  const assetsRes = await axios.get(
    `${Constants.BASE_URL}api/v1/clients/all-system-assets`,
    { params: assetParams }
  );

  const extractPagination = (res) => {
    const totalCount = res.headers["x-total-count"];
    if (totalCount == null) return null;
    return {
      totalCount: parseInt(totalCount, 10),
      totalPages: parseInt(res.headers["x-total-pages"], 10),
      pageNumber: parseInt(res.headers["x-page-number"], 10),
      pageSize: parseInt(res.headers["x-page-size"], 10),
    };
  };

  return {
    clients:
      clientsRes.status === 200 ? clientsRes.data.responseData || [] : [],
    clientsPagination: extractPagination(clientsRes),
    assets: assetsRes.status === 200 ? assetsRes.data.responseData || [] : [],
    assetsPagination: extractPagination(assetsRes),
  };
};

export const registerClient = async (formData, requesterId) => {
  setAuthHeader();

  const response = await axios.post(
    `${Constants.BASE_URL}api/v1/clients`,
    formData,
    { params: { requesterId } }
  );

  if (response.status !== 201 && response.status !== 200) {
    throw new Error("Failed to register client");
  }

  return response.data;
};

export const updateClient = async (clientId, formData, requesterId) => {
  setAuthHeader();

  const response = await axios.put(
    `${Constants.BASE_URL}api/v1/clients/${clientId}`,
    formData,
    { params: { requesterId } }
  );

  if (response.status !== 200) {
    throw new Error("Failed to update client");
  }

  return response.data;
};

export const fetchClientDetails = async (clientId, pagination) => {
  setAuthHeader();

  const assetParams = pagination?.assets
    ? { page: pagination.assets.page, size: pagination.assets.size }
    : {};
  const userParams = pagination?.users
    ? { page: pagination.users.page, size: pagination.users.size }
    : {};

  const assetsRes = await axios.get(
    `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/assets/getAllAssets`,
    { params: assetParams }
  );
  const usersRes = await axios.get(
    `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/users/getAllClientUsers`,
    { params: userParams }
  );

  const extractPagination = (res) => {
    const totalCount = res.headers["x-total-count"];
    if (totalCount == null) return null;
    return {
      totalCount: parseInt(totalCount, 10),
      totalPages: parseInt(res.headers["x-total-pages"], 10),
      pageNumber: parseInt(res.headers["x-page-number"], 10),
      pageSize: parseInt(res.headers["x-page-size"], 10),
    };
  };

  return {
    assets: assetsRes.status === 200 ? assetsRes.data.responseData || [] : [],
    assetsPagination: extractPagination(assetsRes),
    users: usersRes.status === 200 ? usersRes.data.responseData || [] : [],
    usersPagination: extractPagination(usersRes),
  };
};

export const updateClientStatus = async (clientId, status, requesterId) => {
  setAuthHeader();

  await axios.put(
    `${Constants.BASE_URL}api/v1/clients/${clientId}/status`,
    null,
    {
      params: {
        requesterId,
        status,
      },
    }
  );
};

export const updateClientFeatures = async (
  clientId,
  requestBody,
  requesterId
) => {
  setAuthHeader();

  const response = await axios.put(
    `${Constants.BASE_URL}api/v1/clients/${clientId}/features`,
    requestBody,
    { params: { requesterId } }
  );

  if (response.status !== 200) {
    throw new Error("Failed to update features");
  }

  return response.data.responseData;
};

export const updateAssetStatus = async (
  clientId,
  assetId,
  assetStatus,
  userId
) => {
  setAuthHeader();

  const response = await axios.put(
    `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/assets`,
    null,
    {
      params: {
        assetId,
        assetStatus,
        userId,
      },
    }
  );

  if (response.status !== 200) {
    throw new Error("Failed to update asset status");
  }

  return response.data;
};
